import { create } from 'zustand';
import type {
  CountdownSetting,
  FxConfig,
  Marker,
  MicDeviceInfo,
  ProjectDoc,
  RecorderState,
  Track,
} from '../types.js';
import { createProject } from '../audio/projectUtils.js';
import { uid } from '../utils/misc.js';

export type Screen = 'home' | 'studio' | 'projects';
export type PanelTab = 'record' | 'timeline' | 'mix' | 'fx' | 'sync' | 'takes' | 'analysis' | 'export';

export interface ToastMessage {
  id: string;
  title: string;
  description?: string;
  tone: 'success' | 'warning' | 'error' | 'info';
}

export interface StudioState {
  screen: Screen;
  panel: PanelTab;
  project: ProjectDoc | null;
  dirty: boolean;
  saving: boolean;
  savedAt: number | null;
  toast: ToastMessage | null;
  youtubeInput: string;
  youtubePreviewId: string | null;
  youtubeError: string | null;
  sourceError: string | null;
  playerReady: boolean;
  playerState: 'unstarted' | 'ended' | 'playing' | 'paused' | 'buffering' | 'cued' | 'unavailable';
  playerCurrentTime: number;
  playerDuration: number;
  playerStartDelayMs: number;
  micReady: boolean;
  micDevices: MicDeviceInfo[];
  selectedMicId: string;
  micLevel: { peak: number; rms: number };
  micStatus: RecorderState;
  micError: string | null;
  recording: boolean;
  recordingPaused: boolean;
  recordingSeconds: number;
  countdownLabel: string | null;
  selectedRegion: { start: number; end: number } | null;
  selectedTakeId: string | null;
  installPrompt: any | null;
  projectList: any[];
  isSimpleMode: boolean;
  operationBusy: string | null;
  analysisBusy: boolean;
  exportBusy: boolean;
  exportProgress: number;

  setScreen: (screen: Screen) => void;
  setPanel: (panel: PanelTab) => void;
  setProject: (project: ProjectDoc | null, dirty?: boolean) => void;
  updateProject: (updater: Partial<ProjectDoc> | ((project: ProjectDoc) => ProjectDoc), dirty?: boolean) => void;
  createProject: (opts: { title?: string; sourceType: ProjectDoc['sourceType']; youtubeVideoId?: string; youtubeTitle?: string }) => void;
  setDirty: (dirty: boolean) => void;
  setSaving: (saving: boolean, savedAt?: number | null) => void;
  setToast: (toast: Omit<ToastMessage, 'id'> | null) => void;
  setYoutubeInput: (value: string) => void;
  setYoutubePreviewId: (id: string | null) => void;
  setYoutubeError: (value: string | null) => void;
  setSourceError: (value: string | null) => void;
  setPlayer: (patch: Partial<Pick<StudioState, 'playerReady' | 'playerState' | 'playerCurrentTime' | 'playerDuration' | 'playerStartDelayMs'>>) => void;
  setMic: (patch: Partial<Pick<StudioState, 'micReady' | 'micDevices' | 'selectedMicId' | 'micLevel' | 'micStatus' | 'micError'>>) => void;
  setRecording: (patch: Partial<Pick<StudioState, 'recording' | 'recordingPaused' | 'recordingSeconds' | 'countdownLabel'>>) => void;
  setSelectedRegion: (region: { start: number; end: number } | null) => void;
  setSelectedTakeId: (id: string | null) => void;
  setInstallPrompt: (event: any | null) => void;
  setProjectList: (projects: any[]) => void;
  setSimpleMode: (isSimple: boolean) => void;
  setOperationBusy: (value: string | null) => void;
  setAnalysisBusy: (value: boolean) => void;
  setExportBusy: (busy: boolean, progress?: number) => void;
  patchTrack: (trackId: string, patch: Partial<Track>) => void;
  patchTrackFx: (trackId: string, patch: Partial<FxConfig> | ((fx: FxConfig) => FxConfig)) => void;
  addMarker: (time: number, label: string, kind?: Marker['kind']) => void;
  removeMarker: (id: string) => void;
  resetForHome: () => void;
}

const init = {
  screen: 'home' as Screen,
  panel: 'record' as PanelTab,
  project: null,
  dirty: false,
  saving: false,
  savedAt: null,
  toast: null,
  youtubeInput: '',
  youtubePreviewId: null,
  youtubeError: null,
  sourceError: null,
  playerReady: false,
  playerState: 'unstarted' as const,
  playerCurrentTime: 0,
  playerDuration: 0,
  playerStartDelayMs: 0,
  micReady: false,
  micDevices: [] as MicDeviceInfo[],
  selectedMicId: '',
  micLevel: { peak: 0, rms: 0 },
  micStatus: 'idle' as RecorderState,
  micError: null,
  recording: false,
  recordingPaused: false,
  recordingSeconds: 0,
  countdownLabel: null,
  selectedRegion: null,
  selectedTakeId: null,
  installPrompt: null,
  projectList: [] as any[],
  isSimpleMode: true,
  operationBusy: null,
  analysisBusy: false,
  exportBusy: false,
  exportProgress: 0,
};

export const useStudioStore = create<StudioState>((set, get) => ({
  ...init,
  setScreen: (screen) => set({ screen }),
  setPanel: (panel) => set({ panel }),
  setProject: (project, dirty = false) => set({
    project, dirty, screen: project ? 'studio' : get().screen,
    selectedTakeId: null, selectedRegion: null,
    playerReady: false, playerState: 'unstarted', playerCurrentTime: 0, playerDuration: 0,
    playerStartDelayMs: 0, sourceError: null,
    micReady: false, micDevices: [], selectedMicId: '', micLevel: { peak: 0, rms: 0 }, micStatus: 'idle', micError: null,
    recording: false, recordingPaused: false, recordingSeconds: 0, countdownLabel: null,
  }),
  updateProject: (updater, dirty = true) =>
    set((s) => {
      if (!s.project) return {};
      const project = typeof updater === 'function' ? updater(s.project) : { ...s.project, ...updater };
      return { project: { ...project, updatedAt: Date.now() }, dirty };
    }),
  createProject: (opts) => {
    const project = createProject(opts);
    set({ project, screen: 'studio', panel: 'record', dirty: true, playerReady: false, playerState: 'unstarted', playerCurrentTime: 0, playerDuration: 0, sourceError: null, recording: false, recordingPaused: false, recordingSeconds: 0, countdownLabel: null, micReady: false, micDevices: [], selectedMicId: '', micLevel: { peak: 0, rms: 0 }, micStatus: 'idle', micError: null, selectedTakeId: null, selectedRegion: null });
  },
  setDirty: (dirty) => set({ dirty }),
  setSaving: (saving, savedAt = null) => set({ saving, savedAt: savedAt ?? get().savedAt, dirty: saving ? get().dirty : false }),
  setToast: (toast) => set({ toast: toast ? { id: uid('toast'), ...toast } : null }),
  setYoutubeInput: (youtubeInput) => set({ youtubeInput, youtubeError: null }),
  setYoutubePreviewId: (youtubePreviewId) => set({ youtubePreviewId, youtubeError: null }),
  setYoutubeError: (youtubeError) => set({ youtubeError }),
  setSourceError: (sourceError) => set({ sourceError }),
  setPlayer: (patch) => set(patch),
  setMic: (patch) => set(patch),
  setRecording: (patch) => set(patch),
  setSelectedRegion: (selectedRegion) => set({ selectedRegion }),
  setSelectedTakeId: (selectedTakeId) => set({ selectedTakeId }),
  setInstallPrompt: (installPrompt) => set({ installPrompt }),
  setProjectList: (projectList) => set({ projectList }),
  setSimpleMode: (isSimpleMode) => set({ isSimpleMode }),
  setOperationBusy: (operationBusy) => set({ operationBusy }),
  setAnalysisBusy: (analysisBusy) => set({ analysisBusy }),
  setExportBusy: (exportBusy, exportProgress = 0) => set({ exportBusy, exportProgress }),
  patchTrack: (trackId, patch) =>
    set((s) => {
      if (!s.project) return {};
      return {
        project: {
          ...s.project,
          updatedAt: Date.now(),
          tracks: s.project.tracks.map((t) => (t.id === trackId ? { ...t, ...patch } : t)),
        },
        dirty: true,
      };
    }),
  patchTrackFx: (trackId, patch) =>
    set((s) => {
      if (!s.project) return {};
      return {
        project: {
          ...s.project,
          updatedAt: Date.now(),
          tracks: s.project.tracks.map((t) => {
            if (t.id !== trackId) return t;
            const fx = typeof patch === 'function' ? patch(t.fx) : { ...t.fx, ...patch };
            return { ...t, fx };
          }),
        },
        dirty: true,
      };
    }),
  addMarker: (time, label, kind = 'custom') =>
    set((s) => {
      if (!s.project) return {};
      const marker: Marker = { id: uid('marker'), time, label, kind };
      return { project: { ...s.project, markers: [...s.project.markers, marker].sort((a, b) => a.time - b.time), updatedAt: Date.now() }, dirty: true };
    }),
  removeMarker: (id) =>
    set((s) => !s.project ? {} : { project: { ...s.project, markers: s.project.markers.filter((m) => m.id !== id), updatedAt: Date.now() }, dirty: true }),
  resetForHome: () => set({ ...init }),
}));
